import moment from 'moment'
import CacheStorageService from './cacheStorage.service'
import axios from 'axios'

/**
 * Clients / Users with tickets of the client
 * @status no delete
 * @class ClientService
 */
class ClientsService {
    constructor(apiUrl) {
        this.apiUrl = apiUrl
    }

    /**
    * @author Adriana Fernandez
    * @description Get data for the clients dashboard
    * @date 11-06-2024
    * @status no delete
    * @param {String} token
    * @param {Date} startDate
    * @param {Date} endDate
    * @returns {Object}
    * @memberof ClientsService
    */
    async getClientsDashboard(token, startDate, endDate) {
        startDate = moment(startDate).format('YYYY-MM-DD')
        endDate = moment(endDate).format('YYYY-MM-DD')

        const cache = new CacheStorageService();
        const url = new URL(`${this.apiUrl}/client-passthrough`);
        url.searchParams.append('startDate', startDate);
        url.searchParams.append('endDate', endDate);

        const response = await cache.fetchData(url, token);
        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }
        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Get list of clients
    * @date 11-06-2024
    * @status no delete
    * @param {String} token
    * @returns {Object}
    * @memberof ClientsService
    */
    async getClientsList(token) {
        const cache = new CacheStorageService();
        const url = new URL(`${this.apiUrl}/client-list`);

        const response = await cache.fetchData(url, token);
        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }
        return response.data.data
    }

    /**
    * @author Adriana Fernandez
    * @description Get data for the client profile dashboard
    * @date 13-06-2024
    * @status no delete
    * @param {String} token
    * @param {String} userId
    * @param {Date} startDate
    * @param {Date} endDate
    * @param {Integer} part
    * @returns {Object}
    * @memberof ClientsService
    */
    async getClientsProfileDashboard(token, userId, startDate, endDate, part) {
        startDate = moment(startDate).format('YYYY-MM-DD')
        endDate = moment(endDate).format('YYYY-MM-DD')

        const cache = new CacheStorageService();
        const url = new URL(`${this.apiUrl}/client-profile-dashboard`);
        url.searchParams.append('startDate', startDate);
        url.searchParams.append('endDate', endDate);
        url.searchParams.append('userId', userId);
        url.searchParams.append('p', part);

        const response = await cache.fetchData(url, token);
        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }
        return response.data.data
    }

    /**
* @author Adriana Fernandez
* @description Get data for the reports and statistics dashboard
* @date 13-06-2024
* @status no delete
* @param {String} token
* @param {Date} startDate
* @param {Date} endDate
* @param {Integer} part
* @returns {Object}
* @memberof ClientsService
*/
    async getReportsAndStatisticsDashboard(token, startDate, endDate, period) {
        startDate = moment(startDate).format('YYYY-MM-DD')
        endDate = moment(endDate).format('YYYY-MM-DD')

        const cache = new CacheStorageService();
        const url = new URL(`${this.apiUrl}/report-dashboard`);
        url.searchParams.append('startDate', startDate);
        url.searchParams.append('endDate', endDate);
        url.searchParams.append('period', period);

        const response = await cache.fetchData(url, token);
        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }
        return response.data.data
    }

    /**
     * @author Eduardo Roman
     * @description Get data for the ratings and reviews from users
     * @date 30-09-2024 
     * @param {String} token
     * @return {Object} 
     * @memberof ClientsService
     */
    async getRatingsReviewsData(token, startDate, endDate) {
        startDate = moment(startDate).format('YYYY-MM-DD')
        endDate = moment(endDate).format('YYYY-MM-DD')

        const url = new URL(`${this.apiUrl}/rank-tickets`);
        url.searchParams.append('startDate', startDate);
        url.searchParams.append('endDate', endDate);

        let response = await axios.get(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            },
        })
        if (response.data.statusCode !== 200) {
            throw new Error(response.data.message)
        }
        return response.data.data
    }
}

export { ClientsService as default }
